import { getCityList } from "../containers/JobPosting/jobPostingActions";
import {
  DB_EDUCATION_LAST_SHOWN,
  DB_EDUCATION_SHOWN_COUNT,
  ERROR_MESSAGE
} from "./constants";
import CookieService from "./CookieService";
import { Send, UserRoundCheck, Star, Phone } from "lucide-react";
import { isRecommended } from "./utils";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";

// Extend dayjs with advancedFormat plugin
dayjs.extend(advancedFormat);

/**
 * Formats a date using the specified format string
 * @param {string | Date | number} date - The date to format (can be a date string, Date object, or timestamp)
 * @param {string} [format="Do MMM, YYYY"] - The format string (default: "Do MMM, YYYY" e.g., "4th Nov, 2025")
 * @returns {string} - The formatted date string
 *
 * @example
 * formatDate(new Date()) // "4th Nov, 2025"
 * formatDate("2025-11-04", "DD/MM/YYYY") // "04/11/2025"
 * formatDate(1730726400000, "MMM Do, YYYY") // "Nov 4th, 2025"
 */
const formatDate = (date, format = "Do MMM, YYYY") => {
  if (!date) return "";

  try {
    return dayjs(date).format(format);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

function isIOS() {
  if (typeof navigator !== "undefined") {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  }
  return false;
}

const paramsToURL = params => {
  const urlParams = new URLSearchParams();

  const flattenObject = (obj, prefix = "") => {
    for (const key in obj) {
      const newKey = prefix ? `${prefix}_${key}` : key;

      // Handle arrays for new filters as comma-separated values
      if (Array.isArray(obj[key])) {
        if (obj[key].length > 0) {
          urlParams.append(newKey, obj[key].join(","));
        }
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        flattenObject(obj[key], newKey); // Recursively flatten nested objects
      } else {
        urlParams.append(newKey, obj[key]);
      }
    }
  };

  flattenObject(params);
  return urlParams.toString();
};

const urlToParams = queryString => {
  const urlParams = new URLSearchParams(queryString);
  const params = {};
  const setNestedValue = (obj, path, value) => {
    const keys = path.split("_");
    let current = obj;
    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        // Handle comma-separated values for array filters
        if (key === "assets" || key === "skills" || key === "industry") {
          current[key] = value
            ? value.split(",").filter(v => v.trim() !== "")
            : [];
        } else {
          current[key] = value;
        }
      } else {
        current[key] = current[key] || {}; // Ensure the key exists as an object
        current = current[key];
      }
    });
  };
  for (const [key, value] of urlParams.entries()) {
    setNestedValue(params, key, value);
  }
  return params;
};

const getCategoryCityNames = async ({ cityId, categoryId }) => {
  const { options } = await getCityList();
  console.log(options);
};

// Utility function to get initials from a name
function getNameInitials(name) {
  if (!name || typeof name !== "string") return "";

  // Split the string into words and filter out empty entries
  const words = name.trim().split(/\s+/);

  // Get the first letters of the first two words
  const initials = words.slice(0, 2).map(word => word.charAt(0).toUpperCase());

  // Combine the initials
  return initials.join("");
}

// toTitleCase - first letter capitalized, the rest lowercase
function toTitleCase(string) {
  if (string) {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  }
  return "";
}

const getVerificationEventInfo = (verificationStates = {}) => {
  return {
    emailVerified: verificationStates?.email === "completed" ? "Yes" : "No",
    documentUpload: ["pending", "completed"].includes(
      verificationStates?.document
    )
      ? "Yes"
      : "No",
    documentVerified:
      verificationStates?.document === "completed" ? "Yes" : "No"
  };
};

export function getNewErrorMessage(error) {
  return error?.response?.data?.message || ERROR_MESSAGE;
}
function isChromeBrowser() {
  if (typeof window === "undefined") return false;

  const userAgent = window.navigator.userAgent.toLowerCase();

  // Check if browser is Chrome and not other Chromium-based browsers
  const isChrome =
    /chrome/.test(userAgent) &&
    /google inc/.test(window.navigator.vendor.toLowerCase());

  // Exclude other Chromium-based browsers
  const isEdge = /edg/.test(userAgent);
  const isOpera = /opr|opera/.test(userAgent);
  const isBrave = typeof window.navigator.brave !== "undefined";

  return isChrome && !isEdge && !isOpera && !isBrave;
}

const validateImageFile = ({
  file,
  maxSizeInKb = 100,
  allowedTypes = [
    "image/png",
    "image/jpg",
    "image/jpeg",
    "image/svg+xml",
    "image/webp"
  ]
}) => {
  if (!file) return { isValid: false };

  const maxSizeInBytes = maxSizeInKb * 1024;

  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      errorMessage: `File type must be ${allowedTypes
        .map(type => type.split("/")[1])
        .join(", ")}`
    };
  }

  if (file.size > maxSizeInBytes) {
    return {
      isValid: false,
      errorMessage: `File size must not exceed ${maxSizeInKb}kb`
    };
  }

  return { isValid: true };
};

const validateMessagePlaceholders = messageContent => {
  // const containsPlaceholder = /<[^>]+>/.test(messageContent);
  // return containsPlaceholder;
  return /{{(.*?)}}/.test(messageContent);
};

const formatFileSize = bytes => {
  if (!bytes) return "0 KB";

  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${Math.round(kb)} KB`;
  }

  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
};

const convertFileToBase64 = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
    reader.readAsDataURL(file);
  });
};

const generateBroadCastCampaignName = ({ jobId }) => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const timeStamp = `${hours}:${minutes}`;
  return `WA-${jobId}-${timeStamp}`;
};

const checkChromeExtensionInstalled = async extensionId => {
  if (!isChromeBrowser()) return false;

  // Check if chrome runtime exists and has sendMessage
  if (!chrome.runtime || !chrome.runtime.sendMessage) {
    console.warn("Chrome extensions API is not available.");
    return false;
  }

  const result = await new Promise(resolve => {
    try {
      window.chrome.runtime.sendMessage(
        extensionId,
        { type: "check_extension_installed" },
        response => {
          if (chrome.runtime.lastError) {
            console.log(
              "Extension not found:",
              chrome.runtime.lastError.message
            );
            resolve(false);
          } else {
            resolve(response?.installed ?? false);
          }
        }
      );
    } catch (error) {
      console.error("Unexpected error checking extension:", error);
      // Handle "Receiving end does not exist" error
      resolve(false);
    }
  });

  return result;
};

const highlightTemplateElements = content => {
  if (!content) return "Nothing to preview...";

  return content
    .replace(/\n/g, "<br />") // Preserve line breaks
    .replace(/{{(.*?)}}/g, (match, key) => {
      return `<span class="px-1 text-yellow-800" style="background:yellow">{{${key.trim()}}}</span>`;
    });
};

const replacePlaceholders = (content, data = {}) => {
  return content.replace(/{{(.*?)}}/g, (_, key) =>
    key.trim() in data ? data[key.trim()] : `{{${key.trim()}}}`
  );
};

const getFileName = jobId => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = String(now.getFullYear()).slice(-2);
  const time = now.toTimeString().slice(0, 8).replace(/:/g, "-");

  return `JH_${jobId}_${day}${month}${year}_${time}.xlsx`;
};

const getPageNumbersUtil = (totalPages, currentPage) => {
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= currentPage - 1 && i <= currentPage + 1)
    ) {
      pages.push(i);
    } else if (i === currentPage - 2 || i === currentPage + 2) {
      pages.push("ellipsis");
    }
  }
  return pages.filter((page, index, array) => array.indexOf(page) === index);
};

const getFormatedFilters = ({ isFilter, data }) => {
  let filters = data;

  if (isFilter) {
    filters = {
      ...data,
      minExp: data?.experience?.minExp,
      maxExp: data?.experience?.maxExp,
      is_fresher_allowed: data?.experience?.onlyFreshers,
      minSal: data?.salaryRange?.minSal || "",
      maxSal: data?.salaryRange?.maxSal || "",
      includeWithoutSalary: data?.salaryRange?.includeWithoutSalary,
      minAge: data?.ageRange?.minAge || 0,
      maxAge: data?.ageRange?.maxAge || 0,
      includeWithoutAge: data?.ageRange?.includeWithoutAge,
      // Arrays are already parsed as arrays by urlToParams, just ensure they're arrays
      assets: Array.isArray(data?.assets) ? data.assets : [],
      skills: Array.isArray(data?.skills) ? data.skills : [],
      industry: Array.isArray(data?.industry) ? data.industry : [],
      // English fluency is already a simple string, just pass through
      english_required: data?.english_required || "Any"
    };
  } else {
    filters = {
      ...data,
      gender: data?.gender_req,
      qualification: data?.qualification_label,
      jobLocalityId: data?.job_locality_id,
      maxExp: data.maxExp > 72 ? "121" : data.maxExp,
      // Set initial selections from page data when no URL filters
      assets: data?.assets_required_id
        ? data.assets_required_id.map(id => String(id))
        : [],
      skills: data?.skill_required_id
        ? data.skill_required_id.map(id => String(id))
        : [],
      industry: data?.industry_experience_id
        ? data.industry_experience_id.map(id => String(id))
        : []
    };
  }
  return filters;
};

// Helper function to ensure arrays are properly formatted
// Converts objects with numeric keys (from URL params) to arrays
const ensureArray = value => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    // Convert object with numeric keys to array
    return Object.keys(value)
      .sort((a, b) => Number(a) - Number(b))
      .map(key => value[key]);
  }
  return [];
};

// Detect if any of the three hidden filters are applied
const isHiddenFiltersApplied = filters => {
  const HIDDEN_FILTER_IDS = [
    "HIDE_UNLOCKED_CANDIDATES",
    "HIDE_CONTACTED_CANDIDATES",
    "HIDE_ALREADY_DOWNLOADED"
  ];

  const isTruthy = value =>
    value === true || value === "true" || value === 1 || value === "1";

  if (!filters) return false;

  if (Array.isArray(filters)) {
    return filters.some(
      filterItem =>
        HIDDEN_FILTER_IDS.includes(filterItem?.id) &&
        isTruthy(filterItem?.value)
    );
  }

  if (typeof filters === "object") {
    return HIDDEN_FILTER_IDS.some(key => isTruthy(filters?.[key]));
  }

  return false;
};

// Helper function to sort filter options with selected items first
const sortOptionsWithSelectedFirst = (options, selectedIds) => {
  if (!Array.isArray(options)) return [];
  const selectedIdsArray = Array.isArray(selectedIds) ? selectedIds : [];

  return options.sort((a, b) => {
    const aSelected = selectedIdsArray.includes(a.value);
    const bSelected = selectedIdsArray.includes(b.value);

    // If both are selected or both are unselected, maintain original order
    if (aSelected === bSelected) return 0;

    // Put selected items first
    return aSelected ? -1 : 1;
  });
};

const isDbEducationToBeShown = () => {
  const dbEduCount = CookieService.getCookie(DB_EDUCATION_SHOWN_COUNT) ?? 0;
  const dbEduLastShown = CookieService.getCookie(DB_EDUCATION_LAST_SHOWN);
  const today = new Date().toLocaleDateString();

  // If last shown cookie doesn't exist or count is less than 2 and last shown is not today
  return (
    !dbEduLastShown || (Number(dbEduCount) < 2 && dbEduLastShown !== today)
  );
};

const countDbEducationView = () => {
  const dbEduCount = CookieService.getCookie(DB_EDUCATION_SHOWN_COUNT) ?? 0;
  CookieService.setCookie(DB_EDUCATION_SHOWN_COUNT, Number(dbEduCount) + 1);
  CookieService.setCookie(
    DB_EDUCATION_LAST_SHOWN,
    new Date().toLocaleDateString()
  );
};

// Input formatting utilities for form fields - reusable across components
const formatNameInput = value => {
  if (!value) return "";
  let formatted = value.replace(/(^\s*)|(\s*$)/, ""); // Remove leading/trailing spaces

  // Use original getFormattedValue for HTML/emoji removal (but we need to import it)
  // For now, manually remove HTML tags and basic cleanup
  formatted = formatted.replace(/(<([^>]+)>)/gi, ""); // Remove HTML tags
  formatted = formatted.replace(/[^A-Z a-z]/gi, ""); // Only letters and spaces
  formatted = formatted.replace(/\s+/g, " "); // Replace multiple spaces with single space
  return formatted;
};

const formatPhoneInput = value => {
  if (!value) return "";
  let formatted = value.replace(/(^\s*)|(\s*$)/, ""); // Remove leading/trailing spaces

  // Remove HTML tags and non-digits
  formatted = formatted.replace(/(<([^>]+)>)/gi, ""); // Remove HTML tags
  formatted = formatted.replace(/\D/g, ""); // Remove all non-digits
  formatted = formatted.replace(/^0/, ""); // Remove leading 0

  // Limit to 10 digits
  if (formatted.length > 10) {
    formatted = formatted.substring(0, 10);
  }
  return formatted;
};

const formatEmailInput = value => {
  if (!value) return "";
  let formatted = value.replace(/(^\s*)|(\s*$)/, ""); // Remove leading/trailing spaces

  // Remove HTML tags and invalid email characters
  formatted = formatted.replace(/(<([^>]+)>)/gi, ""); // Remove HTML tags
  formatted = formatted.replace(/[^a-zA-Z0-9@._-]/g, ""); // Only valid email characters
  return formatted;
};

/**
 * Sanitizes input for free text fields to prevent HTML attacks and unwanted characters
 * - Removes HTML tags
 * - Removes emojis and unicode symbols
 * - Removes special characters like ^&#*
 * - Allows only alphanumeric characters and spaces
 * - Trims leading/trailing spaces
 * - Replaces multiple consecutive spaces with single space
 * @param {string} value - The input value to sanitize
 * @returns {string} - The sanitized value
 */
const sanitizeFreeTextInput = value => {
  if (!value) return "";

  let sanitized = value.toString(); // Ensure it's a string

  // Remove HTML tags
  sanitized = sanitized.replace(/(<([^>]+)>)/gi, "");

  // Remove HTML entities
  sanitized = sanitized.replace(/&[#\w]+;/g, "");

  // Keep only alphanumeric characters and spaces - remove everything else
  sanitized = sanitized.replace(/[^A-Za-z0-9 ]/g, "");

  // Replace multiple consecutive spaces with single space
  sanitized = sanitized.replace(/\s+/g, " ");

  // Remove leading/trailing spaces
  // sanitized = sanitized.trim();

  return sanitized;
};
/**
 * Calculates the total credits needed for candidate download/unlock, considering free unlocks first.
 * @param {Object} params
 * @param {number} params.selectedCandidatesSize - Total selected candidates
 * @param {number} params.alreadyUnlockedCount - Candidates already unlocked
 * @param {number} params.alreadyDownloadedCount - Candidates already downloaded
 * @param {number} params.perCandidateDownloadPrice - Download price per candidate
 * @param {number} params.perCandidateUnlockPrice - Unlock price per candidate
 * @param {number} [params.freeUnlocksAvailable=0] - Free unlocks available to use first
 * @returns {Object} - { totalCreditsNeeded, paidUnlocks, freeUnlocksUsed, paidDownloads }
 */
function calculateTotalCreditsNeeded({
  selectedCandidatesSize,
  alreadyUnlockedCount,
  alreadyDownloadedCount,
  perCandidateDownloadPrice,
  perCandidateUnlockPrice,
  freeUnlocksAvailable = 0
}) {
  // Free unlocks used is the minimum of freeUnlocksAvailable and (selected - alreadyUnlockedCount)
  const unlocksNeeded = selectedCandidatesSize - alreadyUnlockedCount;
  const freeUnlocksUsed = Math.max(
    0,
    Math.min(freeUnlocksAvailable, unlocksNeeded)
  );
  const paidUnlocks = Math.max(0, unlocksNeeded - freeUnlocksUsed);
  const paidDownloads = Math.max(
    0,
    selectedCandidatesSize - alreadyDownloadedCount
  );

  const totalCreditsNeeded =
    paidUnlocks * perCandidateUnlockPrice +
    paidDownloads * perCandidateDownloadPrice;
  return {
    totalCreditsNeeded,
    paidUnlocks,
    freeUnlocksUsed,
    paidDownloads
  };
}

const isTotalCreditsPlan = plan => {
  return (
    plan === "CANDIDATE_DOWNLOAD" ||
    plan === "CANDIDATE_UNLOCK" ||
    plan === "JOB_BOOST"
  );
};
/**
 * Returns the tag object for candidate subtab, based on applicant details and context.
 * @param {Object} params
 * @param {Object} params.applicantDetails
 * @param {boolean} params.isPartOfDbProduct
 * @param {Object} [params.classes] Optional
 * @returns {Object|null} { id, content, Icon, bgColor, textColor }
 */
export function getCandidateSubtabTag({ applicantDetails, classes = {} }) {
  const {
    js_applied_or_called = null,
    applicant_profile = {},
    status = null
  } = applicantDetails;

  const { is_database_unlock, is_js_unlocked_other_job = false } =
    applicant_profile;
  const renderTag = [
    {
      id: "js-unlocked-other-job",
      match: () => is_js_unlocked_other_job,
      content: "Already unlocked",
      Icon: Star,
      bgColor: classes.recommendationBg,
      textColor: "#EE7682"
    },
    {
      id: "applied",
      match: () => js_applied_or_called === "Applied",
      content: "Direct Applies",
      Icon: Send,
      bgColor: classes.defaultBg,
      textColor: "#4090BE"
    },
    {
      id: "applied-today",
      match: () => js_applied_or_called === "Applied today",
      content: "Applied Today",
      Icon: Send,
      bgColor: classes.appliedTodayBg,
      textColor: "#6366F1"
    },
    {
      id: "js-called-applied",
      match: () =>
        js_applied_or_called &&
        !["Applied", "Applied today"].includes(js_applied_or_called),
      content: js_applied_or_called,
      Icon: Phone,
      bgColor: classes.defaultBg,
      textColor: "#4090BE"
    },
    {
      id: "database",
      match: () => is_database_unlock,
      content: "Database",
      Icon: UserRoundCheck,
      bgColor: classes.databaseBg,
      textColor: "#EE7682"
    },
    {
      id: "recommendation",
      match: () => isRecommended(status),
      content: "Recommended",
      Icon: Star,
      bgColor: classes.recommendationBg,
      textColor: "#EE7682"
    }
  ];

  return renderTag.find(t => t.match()) || null;
}

const isJobFlow = flow => {
  const jobFlowList = [
    "job-purchase",
    "upgrade",
    "job-boost",
    "candidate-download",
    "candidate-unlock"
  ];
  return flow ? jobFlowList.includes(flow) : false;
};

/**
 * Generic function to download a blob response as a file
 * @param {Object} response - The API response containing blob data
 * @param {string} fileName - The name for the downloaded file
 * @param {string} mimeType - The MIME type of the file (default: 'text/csv')
 */
const downloadBlobAsFile = (response, fileName) => {
  try {
    if (!response || !response.data) {
      console.error("Invalid response for file download");
      return;
    }
    // Create blob from response data
    // const blob = new Blob([response.data], { type: mimeType });

    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};

export {
  isIOS,
  paramsToURL,
  urlToParams,
  getCategoryCityNames,
  getNameInitials,
  toTitleCase,
  getVerificationEventInfo,
  isChromeBrowser,
  validateImageFile,
  validateMessagePlaceholders,
  formatFileSize,
  convertFileToBase64,
  generateBroadCastCampaignName,
  checkChromeExtensionInstalled,
  highlightTemplateElements,
  replacePlaceholders,
  getFileName,
  getPageNumbersUtil,
  getFormatedFilters,
  ensureArray,
  sortOptionsWithSelectedFirst,
  isDbEducationToBeShown,
  countDbEducationView,
  formatNameInput,
  formatPhoneInput,
  formatEmailInput,
  sanitizeFreeTextInput,
  calculateTotalCreditsNeeded,
  isTotalCreditsPlan,
  isJobFlow,
  downloadBlobAsFile,
  isHiddenFiltersApplied,
  formatDate
};
